import React, { useState, useEffect, useRef, useCallback } from 'react';
import TodoTask from './TodoTask';
import PrioritySlotsSection from './PrioritySlotsSection';
import ConfirmModal from './ConfirmModal';
import MobileListSelector from './MobileListSelector';
import BuildListForm from './BuildListForm';
import GridView from './GridView';
import KanbanView from './KanbanView';
import MilestoneView from './MilestoneView';
import ShareListModal from './ShareListModal';
import MentionAutocomplete from './MentionAutocomplete';
import { shouldShowList } from '../utils/timeUtils';
import { isDailyHabitsList, ListType } from '../constants/listTypes';
import { api } from '../services/api';

export default function TodoList({
  list,
  isMainList,
  isTomorrowList = false,
  onRenameList = () => Promise.resolve(),
  onAddTask,
  onToggleTask,
  onDeleteTask,
  onPromoteTask,
  onDemoteTask,
  onSnoozeTask,
  onDeleteList,
  onClearCompleted,
  onUpdateTask,
  onReorderTasks,
  lists,
  tomorrowCutoffHour = 18,
  onSelectList,
  onCreateNew,
  onOpenTaskDrawer,
  onReloadLists = null,
  listViewPreferences = {},
  onSaveListViewPreference,
  expandedSubtasksEnabled = false,
  priorityModeEnabled = false
}) {
  const [newTaskText, setNewTaskText] = useState('');
  const [subtasksByTask, setSubtasksByTask] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [deleteTaskModal, setDeleteTaskModal] = useState({ isOpen: false, taskId: null, taskText: null });
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(list.name);
  const [isSavingName, setIsSavingName] = useState(false);
  const nameInputRef = useRef(null);
  const newTaskInputRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [editingTaskIds, setEditingTaskIds] = useState(new Set());
  const itemsSectionRef = useRef(null);
  const [showBuildForm, setShowBuildForm] = useState(false);
  const [viewMode, setViewMode] = useState(() => listViewPreferences[list.id] || 'list');
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    setNameDraft(list.name);
  }, [list.name]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newTaskText.trim() || isMainList || isTomorrowList) return;
    // Allow adding tasks to dailyhabits lists (they will auto-create recurring jobs)

    setIsAdding(true);
    try {
      await onAddTask(list.id, newTaskText.trim());
      setNewTaskText('');
      // Focus the input after adding a task so user can quickly add another
      setTimeout(() => {
        if (newTaskInputRef.current) {
          newTaskInputRef.current.focus();
        }
      }, 0);
    } catch (error) {
      console.error('Error adding task:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleClearCompleted = async () => {
    try {
      const taskIds = completedTasks.map(task => task.id);
      await onClearCompleted(list.id, taskIds);
      setShowClearModal(false);
    } catch (error) {
      console.error('Error clearing completed tasks:', error);
    }
  };

  const handleStartEditName = () => {
    if (isMainList || isTomorrowList) {
      return;
    }
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();

    if (!trimmed) {
      setNameDraft(list.name);
      setIsEditingName(false);
      return;
    }

    if (trimmed === list.name) {
      setIsEditingName(false);
      return;
    }

    try {
      setIsSavingName(true);
      await onRenameList(list.id, trimmed);
      setIsEditingName(false);
    } catch (error) {
      console.error('Error renaming list:', error);
      setNameDraft(list.name);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      setNameDraft(list.name);
      setIsEditingName(false);
    }
  };

  const handleNameBlur = () => {
    if (!isSavingName) {
      handleSaveName();
    }
  };

  const handleShare = async (listId, email) => {
    // TODO: Implement API call to share list
    await api.shareList(listId, email);
    // Reload lists to get updated shared_users
    if (onReloadLists) {
      await onReloadLists();
    }
  };

  const handleUnshare = async (listId, userId) => {
    // TODO: Implement API call to unshare list
    await api.unshareList(listId, userId);
    // Reload lists to get updated shared_users
    if (onReloadLists) {
      await onReloadLists();
    }
  };

  // Separate active and completed tasks
  const activeTasks = list.tasks.filter(task => !task.completed);
  const completedTasks = list.tasks.filter(task => task.completed);

  // Priority slots (main list only, when priority mode is on). Resolved read-only by
  // the backend and attached to the main list as `priority_slots`.
  const prioritySlots = (isMainList && priorityModeEnabled && Array.isArray(list.priority_slots))
    ? list.priority_slots
    : [];
  // Task ids currently shown in filled slots -- filtered OUT of the rendered active
  // list so they aren't duplicated. Filter at render only: `activeTasks` (used by the
  // reorder handler) keeps the slotted tasks so their persisted order is preserved if
  // they later leave a slot.
  const slottedTaskIds = new Set(
    prioritySlots.filter(slot => slot.task).map(slot => slot.task.id)
  );
  const visibleActiveTasks = activeTasks.filter(task => !slottedTaskIds.has(task.id));

  // Check if list is empty and is a user_list
  const isEmpty = activeTasks.length === 0 && completedTasks.length === 0;
  const isUserList = list.list_type === ListType.USER_LIST;
  const shouldShowBuildForm = isEmpty && isUserList && !isMainList && !isTomorrowList;
  
  // Check if grid view is supported (only on desktop, only when supportsGridView is true)
  const supportsGridView = list.supportsGridView && window.innerWidth >= 768;

  // Check if kanban view is supported (user lists on desktop)
  const supportsKanbanView = isUserList && !isMainList && !isTomorrowList && window.innerWidth >= 768;

  // Check if milestone view is supported (same as kanban)
  const supportsMilestoneView = isUserList && !isMainList && !isTomorrowList && window.innerWidth >= 768;

  // Reset to list view if current view mode is no longer supported
  useEffect(() => {
    if (viewMode === 'grid' && !supportsGridView) {
      setViewMode('list');
    }
    if (viewMode === 'kanban' && !supportsKanbanView) {
      setViewMode('list');
    }
    if (viewMode === 'milestone' && !supportsMilestoneView) {
      setViewMode('list');
    }
  }, [supportsGridView, supportsKanbanView, supportsMilestoneView, viewMode]);

  // Restore saved view mode when list changes
  useEffect(() => {
    setViewMode(listViewPreferences[list.id] || 'list');
  }, [list.id, listViewPreferences]);

  // Handle window resize - switch to list view if window becomes too small
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && (viewMode === 'grid' || viewMode === 'kanban')) {
        setViewMode('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const handleSetViewMode = useCallback((mode) => {
    setViewMode(mode);
    if (onSaveListViewPreference) {
      onSaveListViewPreference(list.id, mode);
    }
  }, [list.id, onSaveListViewPreference]);

  const handleSubtaskToggle = async (taskId, subtaskId, newCompleted) => {
    try {
      // Update subtask - GridView already optimistically updated the UI
      await api.updateSubTask(subtaskId, { completed: newCompleted });
      // Optionally reload lists in background to sync (but don't wait for it)
      if (onReloadLists) {
        onReloadLists().catch(err => console.error('Error reloading lists:', err));
      }
    } catch (error) {
      console.error('Error toggling subtask:', error);
      // Error is handled in GridView with revert
      throw error;
    }
  };

  // When the expanded sub-tasks preference is on, fetch every task's sub-tasks
  // for this list in a single call (same endpoint GridView uses) so the list
  // view can render them inline. Refetch when the list or its sub-task counts
  // change (e.g. sub-tasks added/removed in the drawer).
  const subtaskCountSignature = list.tasks.reduce((sum, t) => sum + (t.subTasksCount || 0), 0);
  useEffect(() => {
    if (!expandedSubtasksEnabled) {
      setSubtasksByTask({});
      return;
    }
    let cancelled = false;
    api.getSubTasksForList(list.id)
      .then(map => { if (!cancelled) setSubtasksByTask(map || {}); })
      .catch(err => console.error('Error fetching sub-tasks for list:', err));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id, expandedSubtasksEnabled, subtaskCountSignature]);

  // Toggle a sub-task from the expanded list view, with optimistic update + revert.
  const handleExpandedSubtaskToggle = async (taskId, subtaskId, newCompleted) => {
    setSubtasksByTask(prev => ({
      ...prev,
      [taskId]: (prev[taskId] || []).map(st =>
        st.id === subtaskId ? { ...st, completed: newCompleted } : st
      )
    }));
    try {
      await handleSubtaskToggle(taskId, subtaskId, newCompleted);
    } catch (error) {
      // Revert optimistic update on failure
      setSubtasksByTask(prev => ({
        ...prev,
        [taskId]: (prev[taskId] || []).map(st =>
          st.id === subtaskId ? { ...st, completed: !newCompleted } : st
        )
      }));
    }
  };

  const handleUpdateTaskMetadata = async (taskId, metadata) => {
    try {
      await api.updateTaskMetadata(taskId, metadata);
      if (onReloadLists) {
        onReloadLists().catch(err => console.error('Error reloading lists:', err));
      }
    } catch (error) {
      console.error('Error updating task metadata:', error);
      throw error;
    }
  };

  // Wrapper used by the Kanban/Milestone/Grid views: a view may send a partial
  // ordered subset of IDs (e.g. Grid only renders active tasks with subtasks);
  // append any remaining list.tasks IDs so the backend receives the full order.
  const handleViewReorder = useCallback((listId, partialOrderedIds) => {
    if (!onReorderTasks) return;
    const present = new Set(partialOrderedIds);
    const remainder = list.tasks.map(t => t.id).filter(id => !present.has(id));
    onReorderTasks(listId, [...partialOrderedIds, ...remainder]);
  }, [onReorderTasks, list.tasks]);

  const handleTaskEditingChange = useCallback((taskId, isEditing) => {
    setEditingTaskIds(prev => {
      const next = new Set(prev);
      if (isEditing) {
        next.add(taskId);
      } else {
        next.delete(taskId);
      }
      return next;
    });
  }, []);

  const handleDragStart = (e, taskId) => {
    // Prevent dragging if task is being edited
    if (editingTaskIds.has(taskId)) {
      e.preventDefault();
      return;
    }
    // Use native HTML5 drag-and-drop
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDraggingId(taskId);
  };

  const handleDragOver = (e) => {
    // Necessary to allow a drop
    e.preventDefault();
  };

  const handleDragEnter = (taskId) => {
    if (!draggingId || draggingId === taskId) return;
    setDragOverId(taskId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = (e, targetTaskId) => {
    e.preventDefault();
    const draggedTaskId = e.dataTransfer.getData('text/plain');
    if (!draggedTaskId || draggedTaskId === targetTaskId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    const reorderedActive = Array.from(activeTasks);
    const fromIndex = reorderedActive.findIndex(task => task.id === draggedTaskId);
    const toIndex = reorderedActive.findIndex(task => task.id === targetTaskId);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    const [moved] = reorderedActive.splice(fromIndex, 1);
    reorderedActive.splice(toIndex, 0, moved);

    const newOrder = [...reorderedActive, ...completedTasks].map(task => task.id);
    if (onReorderTasks) {
      onReorderTasks(list.id, newOrder);
    }
    setDraggingId(null);
    setDragOverId(null);
    
    // Fix for browser bug where hover states can get "stuck" after drag-and-drop.
    // During drag, elements that the dragged item passed over can retain hover state
    // even after the drop. We dispatch mouseleave events to clear these stuck states.
    setTimeout(() => {
      if (itemsSectionRef.current) {
        const todoItems = itemsSectionRef.current.querySelectorAll('.todo-item');
        todoItems.forEach(item => {
          const mouseLeaveEvent = new MouseEvent('mouseleave', {
            bubbles: true,
            cancelable: true,
            view: window
          });
          item.dispatchEvent(mouseLeaveEvent);
        });
      }
    }, 0);
  };

  return (
    <div className="todo-card">
      {/* Mobile List Selector in Card Header */}
      <div className={`mobile-list-selector-header ${isMainList ? 'mobile-header-main' : ''} ${isTomorrowList ? 'mobile-header-tomorrow' : ''}`}>
        <MobileListSelector
          lists={lists.filter(l => shouldShowList(l.list_type, tomorrowCutoffHour))}
          activeListId={list.id}
          onSelectList={onSelectList}
          onCreateNew={onCreateNew}
          isMainList={isMainList}
        />
        {!isMainList && !isTomorrowList && (
          <button
            className="mobile-share-btn"
            onClick={() => setShowShareModal(true)}
            title="Share this list"
            aria-label="Share this list"
          >
            Share
          </button>
        )}
      </div>

      {/* Desktop Card Header */}
      <div className={`card-header-custom ${isMainList ? 'card-header-main' : ''} ${isTomorrowList ? 'card-header-tomorrow' : ''}`}>
        <div className="card-header-title-row">
          <h2 className={`card-title ${isMainList ? 'card-title-main' : ''} ${isTomorrowList ? 'card-title-tomorrow' : ''}`}>
            {isMainList || isTomorrowList ? (
              isMainList ? list.name : 'To Do Tomorrow'
            ) : (
              <>
                {isEditingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="card-title-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    onBlur={handleNameBlur}
                    disabled={isSavingName}
                    aria-label="Edit list name"
                  />
                ) : (
                  <span
                    className="card-title-editable"
                    onClick={handleStartEditName}
                    title="Click to rename list"
                  >
                    {list.name}
                    {list.is_shared && (
                      <svg className="shared-icon-title" width="18" height="18" viewBox="0 0 20 20" fill="currentColor" title="Shared list">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    )}
                  </span>
                )}
              </>
            )}
          </h2>
          <div className="card-header-actions">
            {!isMainList && !isTomorrowList && (
              <button
                className="btn-share"
                onClick={() => setShowShareModal(true)}
                title="Share this list"
                aria-label="Share this list"
              >
                Share
              </button>
            )}
            {(supportsGridView || supportsKanbanView || supportsMilestoneView) && (
              <div className="view-toggle-buttons">
                <button
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => handleSetViewMode('list')}
                  title="List view"
                  aria-label="Switch to list view"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M3 4h14a1 1 0 010 2H3a1 1 0 010-2zm0 5h14a1 1 0 010 2H3a1 1 0 010-2zm0 5h14a1 1 0 010 2H3a1 1 0 010-2z" />
                  </svg>
                </button>
                {supportsGridView && (
                  <button
                    className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => handleSetViewMode('grid')}
                    title="Grid view"
                    aria-label="Switch to grid view"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <rect x="2" y="2" width="7" height="7" rx="1.5" />
                      <rect x="11" y="2" width="7" height="7" rx="1.5" />
                      <rect x="2" y="11" width="7" height="7" rx="1.5" />
                      <rect x="11" y="11" width="7" height="7" rx="1.5" />
                    </svg>
                  </button>
                )}
                {supportsKanbanView && (
                  <button
                    className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                    onClick={() => handleSetViewMode('kanban')}
                    title="Kanban view"
                    aria-label="Switch to kanban view"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <rect x="1" y="1" width="5" height="16" rx="1.5" />
                      <rect x="7.5" y="1" width="5" height="8" rx="1.5" />
                      <rect x="14" y="1" width="5" height="12" rx="1.5" />
                    </svg>
                  </button>
                )}
                {supportsMilestoneView && (
                  <button
                    className={`view-toggle-btn ${viewMode === 'milestone' ? 'active' : ''}`}
                    onClick={() => handleSetViewMode('milestone')}
                    title="Milestone view"
                    aria-label="Switch to milestone view"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <circle cx="3" cy="10" r="2.5" />
                      <circle cx="10" cy="10" r="2.5" />
                      <circle cx="17" cy="10" r="2.5" />
                      <rect x="5.5" y="9" width="2" height="2" rx="0.5" />
                      <rect x="12.5" y="9" width="2" height="2" rx="0.5" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-body-custom">
        {!isMainList && !isTomorrowList && (
          <form onSubmit={handleSubmit} className="input-group-custom">
            <MentionAutocomplete
              inputRef={newTaskInputRef}
              value={newTaskText}
              onChange={(e) => setNewTaskText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Add a new task..."
              className="input-custom"
              disabled={isAdding}
              lists={lists || []}
              currentListId={list.id}
            />
            <button
              type="submit"
              disabled={isAdding || !newTaskText.trim()}
              className="btn-custom btn-primary-custom"
            >
              {isAdding ? '...' : 'Add'}
            </button>
          </form>
        )}

        {/* Priority Slots (main list, priority mode on) */}
        {prioritySlots.length > 0 && (
          <PrioritySlotsSection
            slots={prioritySlots}
            listId={list.id}
            onFulfillSlot={(slotId) => api.fulfillPrioritySlot(slotId)}
            onToggleTask={onToggleTask}
            renderTask={(task, onToggleOverride) => (
              <TodoTask
                task={task}
                listId={list.id}
                isMainList={isMainList}
                isTomorrowList={isTomorrowList}
                isDailyHabitsList={isDailyHabitsList(list.list_type)}
                isShared={list.is_shared}
                tomorrowCutoffHour={tomorrowCutoffHour}
                onToggle={onToggleOverride}
                onPromote={onPromoteTask}
                onDemote={onDemoteTask}
                onSnooze={onSnoozeTask}
                onDelete={onDeleteTask}
                onUpdate={onUpdateTask}
                onEditingChange={(isEditing) => handleTaskEditingChange(task.id, isEditing)}
                onOpenDrawer={onOpenTaskDrawer}
                lists={lists}
                expandedSubtasks={expandedSubtasksEnabled}
                subtasks={subtasksByTask[task.id]}
                onSubtaskToggle={handleExpandedSubtaskToggle}
              />
            )}
          />
        )}

        {/* Active Tasks */}
        {shouldShowBuildForm && showBuildForm ? (
          <BuildListForm
            listName={list.name}
            onBuildList={async (description) => {
              await api.buildList(list.id, description, list.name);
              // Reload lists to show the new tasks
              if (onReloadLists) {
                await onReloadLists();
              } else {
                // Fallback: reload the page if callback not provided
                window.location.reload();
              }
              setShowBuildForm(false);
            }}
            onCancel={() => setShowBuildForm(false)}
          />
        ) : activeTasks.length === 0 && completedTasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              {isMainList ? '⭐' : isDailyHabitsList(list.list_type) ? '🔄' : '📝'}
            </div>
            <p className="empty-state-text">
              {isMainList 
                ? 'Promote tasks from your lists to highlight what matters most' 
                : isTomorrowList
                ? 'No tasks scheduled for tomorrow yet'
                : isDailyHabitsList(list.list_type)
                ? 'Add your first task above and it will automatically recur every day! 🎉'
                : 'No tasks yet. Add your first one above!'}
            </p>
            {shouldShowBuildForm && !showBuildForm && (
              <button
                className="btn btn-primary"
                onClick={() => setShowBuildForm(true)}
                style={{ marginTop: '1rem' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M7.5 5.6L5 7l1.4-2.5L5 2l2.5 1.4L10 2 8.6 4.5 10 7 7.5 5.6zm12 9.8L22 14l-1.4 2.5L22 19l-2.5-1.4L17 19l1.4-2.5L17 14l2.5 1.4zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5L22 2zM4.14 21.86a2 2 0 002.83 0L19.78 9.05a2 2 0 000-2.83l-1-1a2 2 0 00-2.83 0L3.14 18.03a2 2 0 000 2.83l1 1z" />
                </svg>
                <span>Help Me Add Tasks</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Kanban View, Grid View, or List View */}
            {viewMode === 'kanban' && supportsKanbanView ? (
              <KanbanView
                tasks={list.tasks}
                onToggleTask={onToggleTask}
                onUpdateTaskMetadata={handleUpdateTaskMetadata}
                onReorderTasks={handleViewReorder}
                listId={list.id}
                onOpenTaskDrawer={onOpenTaskDrawer}
                isShared={list.is_shared}
              />
            ) : viewMode === 'milestone' && supportsMilestoneView ? (
              <MilestoneView
                tasks={list.tasks}
                onToggleTask={onToggleTask}
                onUpdateTaskMetadata={handleUpdateTaskMetadata}
                onReorderTasks={handleViewReorder}
                listId={list.id}
                onOpenTaskDrawer={onOpenTaskDrawer}
                isShared={list.is_shared}
              />
            ) : viewMode === 'grid' && supportsGridView ? (
              <GridView
                tasks={activeTasks}
                listId={list.id}
                onSubtaskToggle={handleSubtaskToggle}
                onReorderTasks={handleViewReorder}
              />
            ) : (
              /* Active Tasks Section (Draggable via native HTML5 API) */
              visibleActiveTasks.length > 0 && (
                <div ref={itemsSectionRef} className="items-section">
                  {visibleActiveTasks.map((task) => {
                    const showDropIndicator = draggingId && dragOverId === task.id;
                    return (
                      <React.Fragment key={task.id}>
                        {showDropIndicator && (
                          <div className="todo-drop-indicator" />
                        )}
                        <div
                          className={`todo-item-draggable-wrapper${draggingId === task.id ? ' todo-item-dragging' : ''}`}
                          draggable={!editingTaskIds.has(task.id)}
                          onDragStart={(e) => handleDragStart(e, task.id)}
                          onDragOver={handleDragOver}
                          onDragEnter={() => handleDragEnter(task.id)}
                          onDragEnd={handleDragEnd}
                          onDrop={(e) => handleDrop(e, task.id)}
                        >
                          <TodoTask
                            task={task}
                            listId={list.id}
                            isMainList={isMainList}
                            isTomorrowList={isTomorrowList}
                            isDailyHabitsList={isDailyHabitsList(list.list_type)}
                            isShared={list.is_shared}
                            tomorrowCutoffHour={tomorrowCutoffHour}
                            onToggle={onToggleTask}
                            onPromote={onPromoteTask}
                            onDemote={onDemoteTask}
                            onSnooze={onSnoozeTask}
                            onDelete={isDailyHabitsList(list.list_type) ? (listId, taskId) => setDeleteTaskModal({ isOpen: true, taskId, taskText: task.text }) : onDeleteTask}
                            onUpdate={onUpdateTask}
                            onEditingChange={(isEditing) => handleTaskEditingChange(task.id, isEditing)}
                            onOpenDrawer={onOpenTaskDrawer}
                            lists={lists}
                            expandedSubtasks={expandedSubtasksEnabled}
                            subtasks={subtasksByTask[task.id]}
                            onSubtaskToggle={handleExpandedSubtaskToggle}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )
            )}

            {/* Completed Tasks Section (hidden in kanban/milestone mode — tasks show inline) */}
            {completedTasks.length > 0 && viewMode !== 'kanban' && viewMode !== 'milestone' && (
              <div className="completed-section">
                <div className="completed-header">
                  <button
                    onClick={() => setShowCompleted(!showCompleted)}
                    className="completed-toggle"
                    aria-label={showCompleted ? 'Hide completed tasks' : 'Show completed tasks'}
                  >
                    <span className="completed-toggle-icon">{showCompleted ? '▼' : '▶'}</span>
                    <span className="completed-toggle-text">
                      Completed ({completedTasks.length})
                    </span>
                  </button>
                  {showCompleted && (
                    <button
                      onClick={() => setShowClearModal(true)}
                      className="btn-clear-completed"
                      title="Delete all completed tasks"
                      aria-label="Delete all completed tasks"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {showCompleted && (
                  <div className="completed-items">
                    {completedTasks.map(task => (
                      <TodoTask
                        key={task.id}
                        task={task}
                        listId={list.id}
                        isMainList={isMainList}
                        isTomorrowList={isTomorrowList}
                        tomorrowCutoffHour={tomorrowCutoffHour}
                        onToggle={onToggleTask}
                        onPromote={onPromoteTask}
                        onDemote={onDemoteTask}
                        onSnooze={onSnoozeTask}
                        onUpdate={onUpdateTask}
                        onOpenDrawer={onOpenTaskDrawer}
                        lists={lists}
                        expandedSubtasks={expandedSubtasksEnabled}
                        subtasks={subtasksByTask[task.id]}
                        onSubtaskToggle={handleExpandedSubtaskToggle}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Clear Completed Modal */}
      <ConfirmModal
        isOpen={showClearModal}
        title="Clear Completed Tasks"
        message={`Are you sure you want to delete all ${completedTasks.length} completed task${completedTasks.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        onConfirm={handleClearCompleted}
        onCancel={() => setShowClearModal(false)}
        isDanger={true}
      />

      {/* Delete Task Modal (for Daily Habits) */}
      <ConfirmModal
        isOpen={deleteTaskModal.isOpen}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTaskModal.taskText}"? This will permanently remove it from your Daily Habits list and the recurring task job.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={async () => {
          if (deleteTaskModal.taskId) {
            await onDeleteTask(list.id, deleteTaskModal.taskId);
            setDeleteTaskModal({ isOpen: false, taskId: null, taskText: null });
          }
        }}
        onCancel={() => setDeleteTaskModal({ isOpen: false, taskId: null, taskText: null })}
        isDanger={true}
      />

      {/* Share List Modal */}
      <ShareListModal
        isOpen={showShareModal}
        listId={list.id}
        listName={list.name}
        sharedUsers={list.shared_users || []}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        onUnshare={handleUnshare}
      />
    </div>
  );
}
